import bcrypt from 'bcryptjs';
import jwt from 'jsonwebtoken';

/**
 * Servicio de Seguridad para Andes Mining Solutions
 * Maneja encriptación, autenticación y validación de sesiones
 */
class SecurityService {
  constructor() {
    this.jwtSecret = process.env.REACT_APP_JWT_SECRET || 'fallback-secret-key';
    this.saltRounds = 12;
    this.sessionTimeout = parseInt(process.env.REACT_APP_SESSION_TIMEOUT) || 3600000; // 1 hora
    this.maxLoginAttempts = parseInt(process.env.REACT_APP_MAX_LOGIN_ATTEMPTS) || 5;
    this.lockoutDuration = parseInt(process.env.REACT_APP_LOCKOUT_DURATION) || 900000; // 15 min
  }

  /**
   * Genera hash seguro de contraseña
   * @param {string} password - Contraseña en texto plano
   * @returns {Promise<string>} Hash de la contraseña
   */
  async hashPassword(password) {
    try {
      if (!password || password.length < 6) {
        throw new Error('La contraseña debe tener al menos 6 caracteres');
      }
      return await bcrypt.hash(password, this.saltRounds);
    } catch (error) {
      console.error('Error al generar hash de contraseña:', error);
      throw new Error('Error en el sistema de seguridad');
    }
  }

  /**
   * Verifica contraseña contra hash
   * @param {string} password - Contraseña en texto plano
   * @param {string} hash - Hash almacenado
   * @returns {Promise<boolean>} True si coinciden
   */
  async verifyPassword(password, hash) {
    try {
      if (!password || !hash) return false;
      return await bcrypt.compare(password, hash);
    } catch (error) {
      console.error('Error al verificar contraseña:', error);
      return false;
    }
  }

  /**
   * Genera token JWT para sesión
   * @param {object} userData - Datos del usuario
   * @returns {string} Token JWT
   */
  generateToken(userData) {
    try {
      const payload = {
        id: userData.email,
        nombre: userData.nombre,
        rol: userData.rol,
        empresa: userData.empresa,
        iat: Date.now(),
        exp: Date.now() + this.sessionTimeout
      };
      return jwt.sign(payload, this.jwtSecret);
    } catch (error) {
      console.error('Error al generar token:', error);
      throw new Error('Error en la generación de sesión');
    }
  }

  /**
   * Verifica y decodifica token JWT
   * @param {string} token - Token JWT
   * @returns {object|null} Datos del usuario o null si inválido
   */
  verifyToken(token) {
    try {
      if (!token) return null;
      const decoded = jwt.verify(token, this.jwtSecret);
      
      // Verificar expiración
      if (decoded.exp < Date.now()) {
        return null;
      }
      
      return decoded;
    } catch (error) {
      console.error('Token inválido:', error);
      return null;
    }
  }

  /**
   * Valida fortaleza de contraseña
   * @param {string} password - Contraseña a validar
   * @returns {object} Resultado de validación
   */
  validatePassword(password) {
    const result = {
      isValid: false,
      errors: [],
      strength: 'weak'
    };

    if (!password) {
      result.errors.push('La contraseña es requerida');
      return result;
    }

    if (password.length < 8) {
      result.errors.push('La contraseña debe tener al menos 8 caracteres');
    }

    if (!/[A-Z]/.test(password)) {
      result.errors.push('Debe contener al menos una letra mayúscula');
    }

    if (!/[a-z]/.test(password)) {
      result.errors.push('Debe contener al menos una letra minúscula');
    }

    if (!/[0-9]/.test(password)) {
      result.errors.push('Debe contener al menos un número');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      result.errors.push('Debe contener al menos un carácter especial');
    }

    // Determinar fortaleza
    const criteriasMet = 5 - result.errors.length;
    if (criteriasMet >= 4) result.strength = 'strong';
    else if (criteriasMet >= 2) result.strength = 'medium';

    result.isValid = result.errors.length === 0;
    return result;
  }

  /**
   * Maneja intentos de login fallidos
   * @param {string} email - Email del usuario
   * @returns {object} Estado del bloqueo
   */
  handleFailedLogin(email) {
    const attemptsKey = `login_attempts_${email}`;
    const lockoutKey = `lockout_${email}`;
    
    const attempts = parseInt(localStorage.getItem(attemptsKey) || '0');
    const lockoutTime = parseInt(localStorage.getItem(lockoutKey) || '0');
    
    // Verificar si está bloqueado
    if (lockoutTime > Date.now()) {
      const remainingTime = Math.ceil((lockoutTime - Date.now()) / 1000 / 60);
      return {
        isLocked: true,
        remainingMinutes: remainingTime,
        message: `Cuenta bloqueada. Intenta en ${remainingTime} minutos.`
      };
    }

    // Incrementar intentos
    const newAttempts = attempts + 1;
    localStorage.setItem(attemptsKey, newAttempts.toString());

    // Si excede el máximo, bloquear
    if (newAttempts >= this.maxLoginAttempts) {
      const lockoutEnd = Date.now() + this.lockoutDuration;
      localStorage.setItem(lockoutKey, lockoutEnd.toString());
      localStorage.removeItem(attemptsKey);
      
      return {
        isLocked: true,
        remainingMinutes: Math.ceil(this.lockoutDuration / 1000 / 60),
        message: `Cuenta bloqueada por ${Math.ceil(this.lockoutDuration / 1000 / 60)} minutos debido a múltiples intentos fallidos.`
      };
    }

    return {
      isLocked: false,
      remainingAttempts: this.maxLoginAttempts - newAttempts,
      message: `Credenciales incorrectas. Te quedan ${this.maxLoginAttempts - newAttempts} intentos.`
    };
  }

  /**
   * Limpia intentos de login después de login exitoso
   * @param {string} email - Email del usuario
   */
  clearFailedAttempts(email) {
    localStorage.removeItem(`login_attempts_${email}`);
    localStorage.removeItem(`lockout_${email}`);
  }

  /**
   * Verifica si un usuario está bloqueado
   * @param {string} email - Email del usuario
   * @returns {boolean} True si está bloqueado
   */
  isUserLocked(email) {
    const lockoutTime = parseInt(localStorage.getItem(`lockout_${email}`) || '0');
    return lockoutTime > Date.now();
  }

  /**
   * Verifica rate limiting para diferentes acciones
   * @param {string} action - Acción a verificar (login, password_reset, etc.)
   * @param {string} identifier - Identificador (email, IP, etc.)
   * @returns {object} Resultado del rate limiting
   */
  checkRateLimit(action, identifier) {
    try {
      const key = `rate_limit_${action}_${identifier}`;
      const now = Date.now();
      
      // Obtener configuración según el entorno
      const isDev = process.env.NODE_ENV === 'development';
      const maxAttempts = action === 'login' 
        ? (isDev ? 5 : 3)
        : (isDev ? 3 : 2);
      const windowMs = 60000; // 1 minuto
      
      // Obtener intentos actuales
      const stored = localStorage.getItem(key);
      let attempts = stored ? JSON.parse(stored) : { count: 0, firstAttempt: now };
      
      // Resetear ventana si ha pasado el tiempo
      if (now - attempts.firstAttempt > windowMs) {
        attempts = { count: 0, firstAttempt: now };
      }
      
      // Verificar si se superó el límite
      if (attempts.count >= maxAttempts) {
        const retryAfter = Math.ceil((windowMs - (now - attempts.firstAttempt)) / 1000);
        return {
          allowed: false,
          retryAfter: retryAfter,
          message: `Demasiados intentos. Inténtalo de nuevo en ${retryAfter} segundos.`
        };
      }
      
      // Incrementar contador
      attempts.count++;
      localStorage.setItem(key, JSON.stringify(attempts));
      
      return {
        allowed: true,
        remainingAttempts: maxAttempts - attempts.count,
        message: 'Intento permitido'
      };
      
    } catch (error) {
      console.error('Error en checkRateLimit:', error);
      // En caso de error, permitir la acción pero loguear
      this.logSecurityEvent('RATE_LIMIT_ERROR', {
        action,
        identifier,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      return { allowed: true, message: 'Rate limit check failed, allowing action' };
    }
  }

  /**
   * Sanitiza entrada de usuario para prevenir XSS
   * @param {string} input - Entrada del usuario
   * @returns {string} Entrada sanitizada
   */
  sanitizeInput(input) {
    if (typeof input !== 'string') return '';
    
    return input
      .replace(/[<>]/g, '') // Remover < y >
      .replace(/javascript:/gi, '') // Remover javascript:
      .replace(/on\w+=/gi, '') // Remover eventos onclick, onload, etc.
      .trim();
  }

  /**
   * Genera un ID único seguro
   * @returns {string} ID único
   */
  generateSecureId() {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 15);
    return `${timestamp}_${randomPart}`;
  }

  /**
   * Valida diferentes tipos de input
   * @param {string} input - Input a validar
   * @param {string} type - Tipo de validación (email, password, text)
   * @returns {object} Resultado de validación
   */
  validateInput(input, type) {
    try {
      if (!input || typeof input !== 'string') {
        return {
          isValid: false,
          error: 'Input requerido',
          sanitized: ''
        };
      }

      const trimmed = input.trim();

      switch (type) {
        case 'email':
          return this.validateEmail(trimmed);
        case 'password':
          return this.validatePasswordSimple(trimmed);
        case 'text':
          return this.validateText(trimmed);
        default:
          return this.validateText(trimmed);
      }
    } catch (error) {
      console.error('Error en validateInput:', error);
      return {
        isValid: false,
        error: 'Error de validación',
        sanitized: this.sanitizeInput(input)
      };
    }
  }

  /**
   * Validación simple de email
   * @param {string} email - Email a validar
   * @returns {object} Resultado de validación
   */
  validateEmail(email) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(email)) {
      return {
        isValid: false,
        error: 'Formato de email inválido',
        sanitized: this.sanitizeInput(email)
      };
    }

    return {
      isValid: true,
      error: null,
      sanitized: email.toLowerCase()
    };
  }

  /**
   * Validación simple de contraseña para login
   * @param {string} password - Contraseña a validar
   * @returns {object} Resultado de validación
   */
  validatePasswordSimple(password) {
    if (password.length < 4) {
      return {
        isValid: false,
        error: 'La contraseña debe tener al menos 4 caracteres',
        sanitized: password
      };
    }

    if (password.length > 128) {
      return {
        isValid: false,
        error: 'La contraseña es demasiado larga',
        sanitized: password.substring(0, 128)
      };
    }

    return {
      isValid: true,
      error: null,
      sanitized: password
    };
  }

  /**
   * Validación de texto general
   * @param {string} text - Texto a validar
   * @returns {object} Resultado de validación
   */
  validateText(text) {
    if (text.length === 0) {
      return {
        isValid: false,
        error: 'Campo requerido',
        sanitized: ''
      };
    }

    if (text.length > 255) {
      return {
        isValid: false,
        error: 'Texto demasiado largo',
        sanitized: text.substring(0, 255)
      };
    }

    // Verificar caracteres peligrosos
    if (/<[^>]*>/.test(text) || /javascript:/i.test(text)) {
      return {
        isValid: false,
        error: 'Contiene caracteres no permitidos',
        sanitized: this.sanitizeInput(text)
      };
    }

    return {
      isValid: true,
      error: null,
      sanitized: text
    };
  }

  /**
   * Log de eventos de seguridad
   * @param {string} event - Tipo de evento
   * @param {object} details - Detalles del evento
   */
  logSecurityEvent(event, details = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      details,
      userAgent: navigator.userAgent,
      ip: 'client-side', // En producción obtener IP real
      sessionId: this.generateSecureId()
    };

    console.log('Security Event:', logEntry);
    
    // En producción, enviar a servicio de logging
    if (process.env.REACT_APP_ENVIRONMENT === 'production') {
      this.sendSecurityLog(logEntry);
    }
  }

  /**
   * Envía logs de seguridad (mock para producción)
   * @param {object} logEntry - Entrada de log
   */
  sendSecurityLog(logEntry) {
    // Implementar envío a servicio de logging en producción
    // fetch('/api/security/logs', { method: 'POST', body: JSON.stringify(logEntry) });
  }

  /**
   * Verifica integridad de datos críticos
   * @param {object} data - Datos a verificar
   * @returns {boolean} True si los datos son íntegros
   */
  verifyDataIntegrity(data) {
    try {
      // Verificar que los campos requeridos están presentes
      const requiredFields = ['email', 'nombre', 'rol'];
      for (const field of requiredFields) {
        if (!data[field]) return false;
      }

      // Verificar formato de email
      if (!this.validateEmail(data.email).isValid) return false;

      // Verificar rol válido
      const validRoles = ['admin', 'cliente'];
      if (!validRoles.includes(data.rol)) return false;

      return true;
    } catch (error) {
      console.error('Error verificando integridad de datos:', error);
      return false;
    }
  }
}

// Exportar instancia singleton
const securityService = new SecurityService();
export default securityService;